import { z } from "zod";

export const conversationWorkflowStatusSchema = z.enum([
  "running",
  "waiting_approval",
  "succeeded",
  "partial_failed",
  "failed",
  "cancelled",
]);

export const conversationWorkflowStepStatusSchema = z.enum([
  "queued",
  "running",
  "waiting_approval",
  "succeeded",
  "failed",
  "cancelled",
  "skipped",
]);

const optionalTime = z.string().datetime({ offset: true }).nullable();
const identifier = z.string().trim().min(1).max(160);
const publicResultId = z.string().max(300);
const terminalWorkflowStatuses = new Set(["succeeded", "partial_failed", "failed", "cancelled"]);
const terminalStepStatuses = new Set(["succeeded", "failed", "cancelled", "skipped"]);

const conversationWorkflowStepSchema = z.object({
  step_id: identifier,
  sequence: z.number().int().positive(),
  capability: identifier,
  kind: z.enum(["listing", "media", "platform_write", "fresh", "tool"]),
  status: conversationWorkflowStepStatusSchema,
  dependency_step_ids: z.array(identifier).max(20),
  attempt: z.number().int().positive(),
  retryable: z.boolean(),
  retry_supported: z.boolean(),
  retry_reason_code: z.string().max(120),
  summary: z.string().max(1_000),
  error_code: z.string().max(120),
  error_message: z.string().max(500),
  source_type: z.enum(["", "creative_media_job", "agent_execution", "agent_async_task"]),
  source_id: publicResultId,
  started_at: optionalTime,
  completed_at: optionalTime,
  revision: z.number().int().positive(),
}).strict();

const conversationWorkflowArtifactSchema = z.object({
  artifact_id: identifier,
  step_id: identifier,
  kind: z.enum(["listing", "media_job", "media_resource", "file", "platform_object"]),
  resource_id: z.string().trim().min(1).max(300),
  label: z.string().max(240),
  mime_type: z.string().max(120),
  metadata_summary: z.record(z.string(), z.unknown()),
  open_action: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("open_media_canvas"),
      job_id: identifier,
      resource_id: publicResultId,
    }).strict(),
    z.object({
      kind: z.literal("open_files"),
      job_id: z.literal(""),
      resource_id: publicResultId,
    }).strict(),
  ]).nullable(),
  open_unavailable_reason: z.string().max(120),
}).strict();

const conversationWorkflowReceiptSchema = z.object({
  receipt_id: identifier,
  step_id: identifier,
  source: z.enum(["tool", "media", "platform_write", "fresh"]),
  status: z.enum(["pending", "succeeded", "failed", "cancelled", "unknown"]),
  public_result_id: publicResultId,
  readback_verified: z.boolean(),
  label: z.string().max(240),
  summary: z.string().max(1_000),
  metadata_summary: z.record(z.string(), z.unknown()),
}).strict();

export const conversationWorkflowSchema = z.object({
  schema: z.literal("conversation_workflow.v1"),
  workflow_id: identifier,
  organization_id: identifier,
  conversation_id: identifier,
  actor_user_id: identifier,
  turn_id: identifier,
  shop_scope_kind: z.enum(["binding", "unbound"]),
  shop_binding_id: identifier.nullable(),
  status: conversationWorkflowStatusSchema,
  revision: z.number().int().positive(),
  started_at: z.string().datetime({ offset: true }),
  completed_at: optionalTime,
  steps: z.array(conversationWorkflowStepSchema).max(64),
  artifacts: z.array(conversationWorkflowArtifactSchema).max(128),
  receipts: z.array(conversationWorkflowReceiptSchema).max(128),
}).strict().superRefine((workflow, context) => {
  if ((workflow.shop_scope_kind === "binding") !== (workflow.shop_binding_id !== null)) {
    context.addIssue({ code: "custom", message: "店铺范围与绑定 ID 不一致", path: ["shop_binding_id"] });
  }
  if (terminalWorkflowStatuses.has(workflow.status) !== (workflow.completed_at !== null)) {
    context.addIssue({ code: "custom", message: "工作流终态与完成时间不一致", path: ["completed_at"] });
  }
  const stepIds = new Set<string>();
  const stepsById = new Map<string, z.infer<typeof conversationWorkflowStepSchema>>();
  const sequences = new Set<number>();
  for (const [index, step] of workflow.steps.entries()) {
    if (stepIds.has(step.step_id) || sequences.has(step.sequence)) {
      context.addIssue({ code: "custom", message: "步骤 ID 与顺序必须唯一", path: ["steps", index] });
    }
    stepIds.add(step.step_id);
    stepsById.set(step.step_id, step);
    sequences.add(step.sequence);
    if ((step.source_type === "") !== (step.source_id === "")) {
      context.addIssue({ code: "custom", message: "步骤来源类型与来源 ID 必须同时存在", path: ["steps", index, "source_id"] });
    }
    if (step.started_at === null || terminalStepStatuses.has(step.status) !== (step.completed_at !== null)) {
      context.addIssue({ code: "custom", message: "步骤状态与时间不一致", path: ["steps", index, "completed_at"] });
    }
  }
  for (const [index, step] of workflow.steps.entries()) {
    if (step.dependency_step_ids.some((id) => id === step.step_id || !stepIds.has(id))) {
      context.addIssue({ code: "custom", message: "步骤依赖必须引用同工作流的其他步骤", path: ["steps", index, "dependency_step_ids"] });
    }
  }
  if (reduceWorkflowStatus(workflow.steps.map((step) => step.status)) !== workflow.status) {
    context.addIssue({ code: "custom", message: "工作流状态必须由步骤状态归约", path: ["status"] });
  }
  const artifactIds = new Set<string>();
  for (const [index, artifact] of workflow.artifacts.entries()) {
    if (artifactIds.has(artifact.artifact_id) || !stepIds.has(artifact.step_id)) {
      context.addIssue({ code: "custom", message: "成果必须唯一且属于当前工作流步骤", path: ["artifacts", index] });
    }
    const action = artifact.open_action;
    const step = stepsById.get(artifact.step_id);
    if (action && action.resource_id !== artifact.resource_id) {
      context.addIssue({ code: "custom", message: "成果打开动作必须引用当前成果", path: ["artifacts", index, "open_action"] });
    } else if (action?.kind === "open_files" && artifact.kind !== "file") {
      context.addIssue({ code: "custom", message: "文件打开动作只能用于文件成果", path: ["artifacts", index, "open_action"] });
    } else if (action?.kind === "open_media_canvas" &&
      (artifact.kind !== "media_job" && artifact.kind !== "media_resource" ||
        !step || step.source_type !== "creative_media_job" || step.source_id !== action.job_id)) {
      context.addIssue({ code: "custom", message: "媒体打开动作必须绑定当前媒体任务", path: ["artifacts", index, "open_action"] });
    }
    artifactIds.add(artifact.artifact_id);
  }
  const receiptIds = new Set<string>();
  for (const [index, receipt] of workflow.receipts.entries()) {
    if (receiptIds.has(receipt.receipt_id) || !stepIds.has(receipt.step_id)) {
      context.addIssue({ code: "custom", message: "回执必须唯一且属于当前工作流步骤", path: ["receipts", index] });
    }
    if (receipt.source === "platform_write" && receipt.status === "succeeded" &&
      (!receipt.readback_verified || !receipt.public_result_id)) {
      context.addIssue({ code: "custom", message: "平台写入成功必须具有官方 ID 与读回证据", path: ["receipts", index] });
    }
    receiptIds.add(receipt.receipt_id);
  }
});

export const conversationWorkflowsResponseSchema = z.object({
  schema: z.literal("conversation_workflow.v1"),
  workflows: z.array(conversationWorkflowSchema),
}).strict();

export const conversationWorkflowEventsResponseSchema = z.object({
  schema: z.literal("conversation_workflow_events.v1"),
  workflow_id: identifier,
  after_revision: z.number().int().nonnegative(),
  current_revision: z.number().int().positive(),
  events: z.array(z.object({
    workflow_id: identifier,
    revision: z.number().int().positive(),
    status: conversationWorkflowStatusSchema,
    occurred_at: z.string().datetime({ offset: true }),
  }).strict()).max(100),
  workflow: conversationWorkflowSchema.nullable(),
}).strict().superRefine((response, context) => {
  let previous = response.after_revision;
  for (const [index, event] of response.events.entries()) {
    if (event.workflow_id !== response.workflow_id || event.revision <= previous || event.revision > response.current_revision) {
      context.addIssue({ code: "custom", message: "工作流事件必须同源且 revision 严格递增", path: ["events", index] });
    }
    previous = event.revision;
  }
  if (response.current_revision < response.after_revision) {
    context.addIssue({ code: "custom", message: "当前 revision 不得早于事件游标", path: ["current_revision"] });
  }
  if (response.workflow && (response.workflow.workflow_id !== response.workflow_id || response.workflow.revision !== response.current_revision)) {
    context.addIssue({ code: "custom", message: "事件快照必须匹配当前工作流 revision", path: ["workflow"] });
  }
  if ((response.current_revision > response.after_revision) !== (response.workflow !== null)) {
    context.addIssue({ code: "custom", message: "revision 推进时必须携带权威快照", path: ["workflow"] });
  }
});

export type ConversationWorkflow = z.infer<typeof conversationWorkflowSchema>;
export type ConversationWorkflowEventsResponse = z.infer<typeof conversationWorkflowEventsResponseSchema>;

export function isConversationWorkflowActive(workflow: ConversationWorkflow) {
  return workflow.status === "running" || workflow.status === "waiting_approval";
}

function reduceWorkflowStatus(statuses: Array<z.infer<typeof conversationWorkflowStepStatusSchema>>) {
  if (statuses.some((status) => status === "queued" || status === "running")) return "running";
  if (statuses.some((status) => status === "waiting_approval")) return "waiting_approval";
  const succeeded = statuses.some((status) => status === "succeeded");
  const failed = statuses.some((status) => status === "failed");
  const cancelled = statuses.some((status) => status === "cancelled");
  if (succeeded && failed) return "partial_failed";
  if (cancelled && !failed) return "cancelled";
  if (failed) return "failed";
  if (succeeded) return "succeeded";
  return "failed";
}
